import React from 'react';
import { ReactComponent as CloseIcon } from '../../../shared/icons/close-icon.svg';
import { ReactComponent as ChatIcon } from '../../../shared/icons/chat-icon.svg';
import { ReactComponent as ChainBrokenIcon } from '../../../shared/icons/chain-broken-icon.svg';
import './mobile-document-header.scss';

interface MobileDocumentHeaderProps {
  onClose?: () => void;
  title: string;
  titleBadge: React.ReactNode;
  sendDropdown?: React.ReactNode;
  onChatClick?: () => void;
  onUnshareClick?: () => void;
  onDownloadClick?: () => void;
  showUnshareButton?: boolean;
  showDownloadButton?: boolean;
}

// Download icon SVG component
const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 11L4 7H6.5V2H9.5V7H12L8 11Z"
      fill="currentColor"
    />
    <path
      d="M2 13H14V15H2V13Z"
      fill="currentColor"
    />
  </svg>
);

export const MobileDocumentHeader: React.FC<MobileDocumentHeaderProps> = ({
  onClose,
  title,
  titleBadge,
  sendDropdown,
  onChatClick,
  onUnshareClick,
  onDownloadClick,
  showUnshareButton = false,
  showDownloadButton = false,
}) => {
  const handleCloseClick = () => {
    onClose?.();
  };

  return (
    <header className="mobile-document-header" data-testid="mobile-document-header">
      {/* Single row with all elements */}
      <div className="mobile-document-header__single-row">
        {onClose && (
          <button
            type="button"
            onClick={handleCloseClick}
            className="mobile-document-header__close-btn"
            data-testid="mobile-document-header__close"
            aria-label="Close document"
          >
            <CloseIcon className="mobile-document-header__close-icon" />
          </button>
        )}

        {/* Title and badge container - will truncate if too long */}
        <div className="mobile-document-header__title-container">
          <h1 className="mobile-document-header__title" title={title}>
            {title}
          </h1>
          <div className="mobile-document-header__badge">{titleBadge}</div>
        </div>

        {/* Chat icon button */}
        {onChatClick && (
          <button
            type="button"
            onClick={onChatClick}
            className="mobile-document-header__chat-btn"
            aria-label="Open AI Chat"
          >
            <ChatIcon />
          </button>
        )}

        {/* Unshare icon button - only show for executed documents with receiver */}
        {showUnshareButton && onUnshareClick && (
          <button
            type="button"
            onClick={onUnshareClick}
            className="mobile-document-header__unshare-icon-btn"
            aria-label="Unshare document"
          >
            <ChainBrokenIcon />
          </button>
        )}

        {/* Download icon button - only show for executed documents */}
        {showDownloadButton && onDownloadClick && (
          <button
            type="button"
            onClick={onDownloadClick}
            className="mobile-document-header__download-icon-btn"
            aria-label="Download signed PDF"
          >
            <DownloadIcon />
          </button>
        )}

        {/* Send dropdown on the right */}
        {sendDropdown && <div className="mobile-document-header__send-dropdown">{sendDropdown}</div>}
      </div>
    </header>
  );
};
